import { Injectable } from '@angular/core';
import Swal from 'sweetalert2';
import { AssessmentService } from './assessment.service';
import { FirebaseService } from './firebase.service';
import { ParticipantInfo, SelfAssessment, AssessmentResult } from '../models/assessment.model';

@Injectable({
  providedIn: 'root'
})
export class SweetAlertWizardService {
  private participantInfo: ParticipantInfo = {};
  private selfAssessment: SelfAssessment = {
    intellectualAdvice: 0,
    formalQualifications: 0,
    financialInvestment: 0,
    longTermAssets: 0,
    roleModel: 0,
    networkConnector: 0,
    leadershipRoles: 0,
    mentorTrainer: 0
  };

  constructor(
    private assessmentService: AssessmentService,
    private firebaseService: FirebaseService
  ) {}

  async startWizard(): Promise<void> {
    const steps = [
      'participant-info',
      'intellectual-ability-1',
      'intellectual-ability-2', 
      'financial-capacity-1',
      'financial-capacity-2',
      'social-influence-1',
      'social-influence-2',
      'professional-position-1',
      'professional-position-2',
      'results'
    ];

    const swalQueueStep = Swal.mixin({
      confirmButtonText: 'Next →',
      cancelButtonText: '← Back',
      showCancelButton: true,
      progressSteps: ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10'],
      reverseButtons: true,
      customClass: {
        container: 'wizard-container',
        popup: 'wizard-popup',
        title: 'wizard-title',
        // content: 'wizard-content',
        actions: 'wizard-actions',
        confirmButton: 'wizard-confirm-btn',
        cancelButton: 'wizard-cancel-btn'
      }
    });

    const values: any[] = [];
    let currentStep = 0;

    const showStep = async (stepIndex: number): Promise<void> => {
      const step = steps[stepIndex];
      let result;

      switch (step) {
        case 'participant-info':
          result = await swalQueueStep.fire({
            title: 'Participant Information',
            html: `
              <div class="wizard-step">
                <p class="step-description">This section is optional. Feel free to skip if you prefer to remain anonymous.</p>
                <div class="form-group">
                  <label for="swal-input1">Name</label>
                  <input id="swal-input1" class="swal2-input" placeholder="Enter your full name" value="${this.participantInfo.name || ''}">
                </div>
                <div class="form-group">
                  <label for="swal-input2">Email Address</label>
                  <input id="swal-input2" class="swal2-input" type="email" placeholder="Enter your email address" value="${this.participantInfo.email || ''}">
                </div>
                <div class="form-group">
                  <label for="swal-input3">Organisation / Table No.</label>
                  <input id="swal-input3" class="swal2-input" placeholder="Enter your organisation or table number" value="${this.participantInfo.organisation || ''}">
                </div>
              </div>
            `,
            currentProgressStep: stepIndex,
            preConfirm: () => {
              const name = (document.getElementById('swal-input1') as HTMLInputElement).value;
              const email = (document.getElementById('swal-input2') as HTMLInputElement).value;
              const organisation = (document.getElementById('swal-input3') as HTMLInputElement).value;
              
              this.participantInfo = { name, email, organisation };
              return { name, email, organisation };
            }
          });
          break;

        case 'intellectual-ability-1':
          result = await swalQueueStep.fire({
            title: 'Intellectual Ability',
            html: `
              <div class="wizard-step">
                <p class="step-description">Please rate from 1 (Strongly Disagree) to 5 (Strongly Agree)</p>
                <div class="question-group">
                  <p class="question-text">I am regularly asked for my opinion, help, or advice in my area of expertise.</p>
                  <div class="rating-scale">
                    ${this.generateRatingScale('intellectual-advice', this.selfAssessment.intellectualAdvice)}
                  </div>
                </div>
              </div>
            `,
            currentProgressStep: stepIndex,
            preConfirm: () => {
              const value = this.getSelectedRating('intellectual-advice');
              this.selfAssessment.intellectualAdvice = value;
              return value;
            }
          });
          break;

        case 'intellectual-ability-2':
          result = await swalQueueStep.fire({
            title: 'Intellectual Ability',
            html: `
              <div class="wizard-step">
                <p class="step-description">Please rate from 1 (Strongly Disagree) to 5 (Strongly Agree)</p>
                <div class="question-group">
                  <p class="question-text">I have formal qualifications, certifications, or licenses in my profession.</p>
                  <div class="rating-scale">
                    ${this.generateRatingScale('formal-qualifications', this.selfAssessment.formalQualifications)}
                  </div>
                </div>
              </div>
            `,
            currentProgressStep: stepIndex,
            preConfirm: () => {
              const value = this.getSelectedRating('formal-qualifications');
              this.selfAssessment.formalQualifications = value;
              return value;
            }
          });
          break;

        case 'financial-capacity-1':
          result = await swalQueueStep.fire({
            title: 'Financial Capacity',
            html: `
              <div class="wizard-step">
                <p class="step-description">Please rate from 1 (Strongly Disagree) to 5 (Strongly Agree)</p>
                <div class="question-group">
                  <p class="question-text">I have the ability to donate or invest financially in community development projects.</p>
                  <div class="rating-scale">
                    ${this.generateRatingScale('financial-investment', this.selfAssessment.financialInvestment)}
                  </div>
                </div>
              </div>
            `,
            currentProgressStep: stepIndex,
            preConfirm: () => {
              const value = this.getSelectedRating('financial-investment');
              this.selfAssessment.financialInvestment = value;
              return value;
            }
          });
          break;

        case 'financial-capacity-2':
          result = await swalQueueStep.fire({
            title: 'Financial Capacity',
            html: `
              <div class="wizard-step">
                <p class="step-description">Please rate from 1 (Strongly Disagree) to 5 (Strongly Agree)</p>
                <div class="question-group">
                  <p class="question-text">I have savings, property, or other long-term assets.</p>
                  <div class="rating-scale">
                    ${this.generateRatingScale('long-term-assets', this.selfAssessment.longTermAssets)}
                  </div>
                </div>
              </div>
            `,
            currentProgressStep: stepIndex,
            preConfirm: () => {
              const value = this.getSelectedRating('long-term-assets');
              this.selfAssessment.longTermAssets = value;
              return value;
            }
          });
          break;

        case 'social-influence-1':
          result = await swalQueueStep.fire({
            title: 'Social Influence',
            html: `
              <div class="wizard-step">
                <p class="step-description">Please rate from 1 (Strongly Disagree) to 5 (Strongly Agree)</p>
                <div class="question-group">
                  <p class="question-text">I am regarded as a role model or respected figure in my social or religious circles.</p>
                  <div class="rating-scale">
                    ${this.generateRatingScale('role-model', this.selfAssessment.roleModel)}
                  </div>
                </div>
              </div>
            `,
            currentProgressStep: stepIndex,
            preConfirm: () => {
              const value = this.getSelectedRating('role-model');
              this.selfAssessment.roleModel = value;
              return value;
            }
          });
          break;

        case 'social-influence-2':
          result = await swalQueueStep.fire({
            title: 'Social Influence',
            html: `
              <div class="wizard-step">
                <p class="step-description">Please rate from 1 (Strongly Disagree) to 5 (Strongly Agree)</p>
                <div class="question-group">
                  <p class="question-text">I often connect people in my network to opportunities, resources, or key individuals.</p>
                  <div class="rating-scale">
                    ${this.generateRatingScale('network-connector', this.selfAssessment.networkConnector)}
                  </div>
                </div>
              </div>
            `,
            currentProgressStep: stepIndex,
            preConfirm: () => {
              const value = this.getSelectedRating('network-connector');
              this.selfAssessment.networkConnector = value;
              return value;
            }
          });
          break;

        case 'professional-position-1':
          result = await swalQueueStep.fire({
            title: 'Professional Position',
            html: `
              <div class="wizard-step">
                <p class="step-description">Please rate from 1 (Strongly Disagree) to 5 (Strongly Agree)</p>
                <div class="question-group">
                  <p class="question-text">I hold or have held leadership roles in an organization, business, or institution.</p>
                  <div class="rating-scale">
                    ${this.generateRatingScale('leadership-roles', this.selfAssessment.leadershipRoles)}
                  </div>
                </div>
              </div>
            `,
            currentProgressStep: stepIndex,
            preConfirm: () => {
              const value = this.getSelectedRating('leadership-roles');
              this.selfAssessment.leadershipRoles = value;
              return value;
            }
          });
          break;

        case 'professional-position-2':
          result = await swalQueueStep.fire({
            title: 'Professional Position',
            html: `
              <div class="wizard-step">
                <p class="step-description">Please rate from 1 (Strongly Disagree) to 5 (Strongly Agree)</p>
                <div class="question-group">
                  <p class="question-text">I mentor, train, or manage others in my professional field or community activities.</p>
                  <div class="rating-scale">
                    ${this.generateRatingScale('mentor-trainer', this.selfAssessment.mentorTrainer)}
                  </div>
                </div>
              </div>
            `,
            currentProgressStep: stepIndex,
            preConfirm: () => {
              const value = this.getSelectedRating('mentor-trainer');
              this.selfAssessment.mentorTrainer = value;
              return value;
            }
          });
          break;

        case 'results':
          const totalScore = this.calculateTotalScore();
          const level = this.determineLevel(totalScore);
          const levelInfo = this.assessmentService.getAssessmentLevel(level);
          
          // Save to services
          this.assessmentService.updateParticipantInfo(this.participantInfo);
          this.assessmentService.updateSelfAssessment(this.selfAssessment);
          
          // Save to Firebase
          try {
            const assessmentResult: AssessmentResult = {
              participantInfo: this.participantInfo,
              selfAssessment: this.selfAssessment,
              totalScore,
              level,
              timestamp: new Date()
            };
            await this.firebaseService.saveAssessment(assessmentResult);
          } catch (error) {
            console.error('Failed to save assessment:', error);
          }

          result = await swalQueueStep.fire({
            title: 'Your Assessment Results',
            html: `
              <div class="results-container">
                <div class="score-circle">
                  <span class="score-number">${totalScore}</span>
                  <span class="score-label">Total Score</span>
                </div>
                <div class="level-info">
                  <h3 class="level-name">${levelInfo?.name || 'Unknown Level'}</h3>
                  <p class="level-message">${levelInfo?.message || ''}</p>
                  <div class="table-assignment">
                    <strong>Please visit ${levelInfo?.table || 'your assigned table'}</strong>
                    <p>${levelInfo?.description || ''}</p>
                  </div>
                </div>
              </div>
            `,
            currentProgressStep: stepIndex,
            showCancelButton: false
          });

          if (result && result.isConfirmed) {
            await this.showThankYou();
          }
          return;
      }

      if (result && result.isConfirmed) {
        values[stepIndex] = result.value;
        currentStep = stepIndex + 1;
        if (currentStep < steps.length) {
          await showStep(currentStep);
        }
      } else if (result && result.dismiss === Swal.DismissReason.cancel && stepIndex > 0) {
        currentStep = stepIndex - 1;
        await showStep(currentStep);
      }
    };

    await showStep(0);
  }

  private async showThankYou(): Promise<void> {
    await Swal.fire({
      title: 'Thank You!',
      html: `
        <div class="thank-you-container">
          <div class="thank-you-icon">
            <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M9 12l2 2 4-4"/>
              <circle cx="12" cy="12" r="10"/>
            </svg>
          </div>
          <p class="thank-you-message">
            Thank you for your time and honesty. Stay connected — together, we build the future.
          </p>
          <div class="next-steps">
            <p>Please proceed to your designated table to connect with others at your engagement level and learn about opportunities that match your skills and interests.</p>
          </div>
        </div>
      `,
      confirmButtonText: 'Take Assessment Again',
      showCancelButton: true,
      cancelButtonText: 'Close',
      customClass: {
        container: 'thank-you-container-swal',
        popup: 'thank-you-popup',
        confirmButton: 'thank-you-confirm-btn',
        cancelButton: 'thank-you-cancel-btn'
      }
    }).then((result) => {
      if (result.isConfirmed) {
        this.resetAssessment();
        this.startWizard();
      }
    });
  }

  private generateRatingScale(name: string, selectedValue: number): string {
    const labels = ['Strongly Disagree', 'Disagree', 'Neutral', 'Agree', 'Strongly Agree'];
    let html = '<div class="rating-options">';
    
    for (let i = 1; i <= 5; i++) {
      const checked = selectedValue === i ? 'checked' : '';
      html += `
        <div class="rating-option">
          <input type="radio" id="${name}-${i}" name="${name}" value="${i}" ${checked}>
          <label for="${name}-${i}">
            <span class="rating-number">${i}</span>
            <span class="rating-label">${labels[i-1]}</span>
          </label>
        </div>
      `;
    }
    
    html += '</div>';
    return html;
  }

  private getSelectedRating(name: string): number {
    const selected = document.querySelector(`input[name="${name}"]:checked`) as HTMLInputElement;
    return selected ? parseInt(selected.value) : 1;
  }

  private calculateTotalScore(): number {
    return Object.values(this.selfAssessment).reduce((sum, value) => sum + value, 0);
  }

  private determineLevel(score: number): 'seed' | 'settlement' | 'organisation' | 'leadership' {
    if (score >= 8 && score <= 15) return 'seed';
    if (score >= 16 && score <= 24) return 'settlement';
    if (score >= 25 && score <= 33) return 'organisation';
    return 'leadership';
  }

  private resetAssessment(): void {
    this.participantInfo = {};
    this.selfAssessment = {
      intellectualAdvice: 0,
      formalQualifications: 0,
      financialInvestment: 0,
      longTermAssets: 0,
      roleModel: 0,
      networkConnector: 0,
      leadershipRoles: 0,
      mentorTrainer: 0
    };
    this.assessmentService.resetAssessment();
  }
}